/**
 * tm/flow/flowLogic.ts — 업무 진행 화면 공용 계산 (대시보드 · 업체별 · /m) — 2026-10-12 업무진행 설계 §2 · §5
 *  - 업무 하나 = 한 줄: 「지금 단계」(완료 아닌 첫 단계)만 본다 · 뒤 단계 날짜는 계획일 뿐
 *  - 내 차례 = 지금 단계가 진행/대기 + 업무가 진행 · 회신 대기 = 지금 단계 회신대기 또는 업무 FP요청대기 (경과에 안 들어감)
 *  - 보류 · 중단 · 완료 업무는 목록에서 빠진다
 */
import type { Company, Task, TaskStep } from '../types';

export interface FlowExtras {
  done_all?: boolean;   // 「업무 전체 완료」로 한꺼번에 끝낸 단계 — 결과를 묻지 않는다
  wait_for?: string[]; wait_since?: string; wait_question?: string;
  postponed?: number; fixed_due?: boolean; nudged_at?: string; done_at?: string; replied_by?: string; replied_at?: string;
  claimed_by?: string; claimed_at?: string | null; claimed_via?: string;   // 대표님 · 팀장님이 「답하기」를 눌렀다(2026-10-12)
  result?: 'yes' | 'no' | ''; result_word?: string; result_reason?: string; result_at?: string;   // 심사 단계 결과(2026-10-12 선정 · 미선정)
}

/* ── 심사 단계 결과 — 선정 · 미선정 / 승인 · 미승인 / 통과 · 미통과 (2026-10-12 지도사 「고객이나 소개해 준 FP님이 난감하지 않은 용어로」)
 *  「탈락 · 실패」는 어디에도 쓰지 않는다. 미○○ 이면 업무 = 「이번 회차 마감」(상태는 중단 그대로 · stop_reason 머리말로 구분 — 업체 포기 「중단」과 따로 셈)
 *  뒤 단계는 「다음 회차에」 · 고객 · 팀장님 화면엔 이유 없이 「이번 회차는 선정되지 않았어요」 */
export interface JudgeWords { yes: string; no: string }
/** 결과 단계 말 — 단계 설정(extras.judge)만 본다(2026-10-28 지도사 「통과 · 승인 같은 용어 하드코딩 안 돼 · 내가 단계를 추가할 수도」) · 설정 › 업무 템플릿 · 단계 편집에서 고름 · 서버 inputCheck.judgeOf 와 같음 */
export function judgeOf(s: { extras?: unknown } | null | undefined): JudgeWords | null {
  if (!s) return null;
  const j = (stepEx(s as TaskStep) as { judge?: { yes?: string; no?: string } | null }).judge;
  const yes = j && typeof j === 'object' ? String(j.yes || '').trim().slice(0, 10) : '';
  return yes ? { yes, no: String((j && j.no) || '').trim().slice(0, 10) || `미${yes.slice(0, 9)}` } : null;
}
/** 「결과」를 꼭 알아야 하는 단계 — 결과 · 선정 · 승인 · 합격 · 최종(중간 심사는 넘어갔으면 통과) · 서버 services/inputCheck FINAL_RX 와 같음 */
export const FINAL_RX = /결과|선정|승인|합격|최종/;
/** 결과를 골라야 하는 단계인가(끝낼 때 ✓ 완료 대신 승인 · 미승인) */
export function needsResult(_t: { type?: string | null }, s: { name?: string | null; extras?: unknown }): boolean { return !!judgeOf(s); }
export const CLOSE_PREFIX = '이번 회차 마감';
export const CLOSE_REASONS = ['서류 보완 필요', '요건 다시 확인', '평가 결과', '예산 조기 마감'];
export function isClosed(t: { status: string; stop_reason?: string | null }): boolean { return t.status === '중단' && String(t.stop_reason || '').startsWith(CLOSE_PREFIX); }
/** 단계 이름 삭선 — 끝낸 단계만 · 이번 회차를 마감한 업무는 긋지 않음(다음 회차에 다시 밟는 단계 · 2026-10-29 지도사 「마감했으면 삭선 지워야지」) · 결과가 아니오(미선정 · 미승인)인 단계도 긋지 않음 */
export function stepStruck(t: { status: string; stop_reason?: string | null }, s: { status?: string; extras?: unknown }): boolean {
  if (s.status !== '완료' || isClosed(t)) return false;
  return stepEx(s as TaskStep).result !== 'no';
}
/** 단계 줄 배지 — 결과 단계(미선정 · 통과) · 마감 뒤 단계 「다음 회차에」 · 해당 없으면 null */
export function roundBadge(t: Task, s: TaskStep): { bg: string; color: string; label: string } | null {
  const ex = stepEx(s);
  if (ex.result === 'no') return { bg: '#E2E8F0', color: '#334155', label: ex.result_word || '미선정' };
  if (ex.result === 'yes' && s.status === '완료') return { bg: '#DCFCE7', color: '#166534', label: ex.result_word || '완료' };
  if (!isClosed(t) || s.status === '완료') return null;
  const ss = sortedSteps(t);
  const at = ss.findIndex(x => stepEx(x).result === 'no');
  return at >= 0 && ss.indexOf(s) > at ? { bg: '#F8FAFC', color: '#94A3B8', label: '다음 회차에' } : null;
}

/** 지금 회신 대기 중에 「답 보냈어요」를 누른 사람 — 업체 · FP · 없으면 '' */
export function claimOf(ex: FlowExtras): string {
  const at = d10(ex.claimed_at || '');
  return ex.claimed_by && at && at >= d10(ex.wait_since || '') ? ex.claimed_by : '';
}
export function claimText(ex: FlowExtras): string {
  const by = claimOf(ex);
  if (!by) return '';
  return `${by === 'FP' ? '팀장님' : '대표님'} ${md(ex.claimed_at)} ${ex.claimed_via === 'sms' ? '문자' : '카톡'}로 답했대요`;
}

export type RowKind = 'mine' | 'wait' | 'off';
/** 업무 담당 (2026-10-18 지도사 「담당자 칸 — 알아서 해봐」) — tasks.assigned_to(이름) · 비면 실무 담당 오재인 · 서버 services/inputCheck.whoOf 와 같음 */
export const STAFF = ['오재인', '임우승'];
export const DEFAULT_WHO = '오재인';
/** 담당이 비었을 때 — 대출 · 연구소 · 메인비즈는 오재인, 나머지는 임우승(2026-10-27 지도사) · 서버 services/inputCheck.ownerOf 와 같음 */
export const STAFF_RX = /대출|정책\s*자금|운전\s*자금|시설\s*자금|구조\s*개선|진로\s*제시|보증|융자|이차\s*보전|중진공|소진공|연구소|연구\s*개발\s*전담|전담\s*부서|메인\s*비즈/;
export function ownerOf(t: { type?: string | null; category_path?: unknown }): string {
  const cat = Array.isArray(t.category_path) ? t.category_path.join(' ') : String(t.category_path || '');
  return STAFF_RX.test(`${t.type || ''} ${cat}`) ? '오재인' : '임우승';
}
export function whoOf(t: { assigned_to?: string | null; type?: string | null; category_path?: unknown }): string { return String(t.assigned_to || '').trim() || ownerOf(t); }

export interface FlowRow {
  task: Task; comp: Company; step: TaskStep | null; kind: RowKind; who: string;
  due: string; d: number | null; waitDays: number; postponed: number; ex: FlowExtras;
}

const WD = ['일', '월', '화', '수', '목', '금', '토'];

export function kstToday(now: number = Date.now()): string { return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10); }
export function d10(v: string | null | undefined): string { return v ? String(v).slice(0, 10) : ''; }
function utc(d: string): number { return Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)); }
export function diffDays(a: string, b: string): number { return Math.round((utc(b) - utc(a)) / 86400000); }
export function addDays(d: string, n: number): string { return new Date(utc(d) + n * 86400000).toISOString().slice(0, 10); }
/** 10/16(금) — 요일은 계산(손으로 적지 않는다) */
export function md(v: string | null | undefined): string {
  const d = d10(v); if (!d) return '';
  return `${+d.slice(5, 7)}/${+d.slice(8, 10)}(${WD[new Date(utc(d)).getUTCDay()]})`;
}

export function stepEx(s: TaskStep | null | undefined): FlowExtras {
  if (!s || !s.extras) return {};
  const x = s.extras as unknown;
  if (typeof x === 'string') { try { return (JSON.parse(x) || {}) as FlowExtras; } catch { return {}; } }
  return x as FlowExtras;
}

export function sortedSteps(t: Task): TaskStep[] {
  return (t.steps || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}
export function curStep(t: Task): TaskStep | null { return sortedSteps(t).find(s => s.status !== '완료') || null; }
/** 결과가 비어 있는 결과 단계 — 단계가 다 끝났는데 「결과 확인」을 ✓ 완료로만 눌러 둔 것(2026-10-18 · 중진공 운전자금 1차 결과 확인) */
export function missingResult(t: Task): TaskStep | null {
  const ss = sortedSteps(t);
  if (ss.some(s => s.status !== '완료')) return null;
  for (let i = ss.length - 1; i >= 0; i -= 1) { if (needsResult(t, ss[i])) { const e = stepEx(ss[i]); return e.result || e.done_all ? null : ss[i]; } }
  return null;
}
export function isOff(t: Task): boolean { return t.status === '완료' || t.status === '중단' || t.status === '보류'; }

export function rowOf(task: Task, comp: Company, today: string = kstToday()): FlowRow {
  const step = curStep(task);
  const ex = stepEx(step);
  const base = { task, comp, step, ex, postponed: Number(ex.postponed) || 0, who: whoOf(task) };
  if (isOff(task) || (!step && task.status !== 'FP요청대기')) return { ...base, kind: 'off', due: '', d: null, waitDays: 0 };
  if (task.status === 'FP요청대기' || (step && step.status === '회신대기')) {
    const since = d10(ex.wait_since) || d10(task.updated_at) || today;
    return { ...base, kind: 'wait', due: '', d: null, waitDays: Math.max(0, diffDays(since, today)) };
  }
  // 2000~2100년 밖 날짜(달력에서 연도를 치는 도중 저장된 0065-01-06 등)는 기한 없음으로 — 「22559일 지남」 방지(2026-10-12)
  const sane = (d: string) => (d >= '2000-01-01' && d <= '2100-12-31' ? d : '');
  const due = sane(d10(step && step.due)) || sane(d10(task.deadline));
  return { ...base, kind: 'mine', due, d: due ? diffDays(today, due) : null, waitDays: 0 };
}

export function allRows(tasks: Task[], companies: Company[], today: string = kstToday()): FlowRow[] {
  const byId = new Map(companies.map(c => [c.id, c]));
  const out: FlowRow[] = [];
  tasks.forEach(t => { const c = byId.get(t.company_id); if (c) out.push(rowOf(t, c, today)); });
  return out;
}

export interface Buckets { overdue: FlowRow[]; today: FlowRow[]; week: FlowRow[]; next: FlowRow[]; wait: FlowRow[]; oldWait: number; claimed: number }
export function buckets(rows: FlowRow[]): Buckets {
  const mine = rows.filter(r => r.kind === 'mine' && r.d !== null);
  const by = (a: FlowRow, b: FlowRow) => (a.d as number) - (b.d as number);
  // 「답 왔대요」(확인만 하면 되는 것)가 맨 위 · 그다음 오래 기다린 순
  const wait = rows.filter(r => r.kind === 'wait').sort((a, b) => (claimOf(b.ex) ? 1 : 0) - (claimOf(a.ex) ? 1 : 0) || b.waitDays - a.waitDays);
  return {
    overdue: mine.filter(r => (r.d as number) < 0).sort(by),
    today: mine.filter(r => r.d === 0),
    week: mine.filter(r => (r.d as number) >= 1 && (r.d as number) <= 7).sort(by),
    next: mine.filter(r => (r.d as number) >= 8 && (r.d as number) <= 14).sort(by),
    wait, oldWait: wait.filter(r => r.waitDays >= 14).length, claimed: wait.filter(r => !!claimOf(r.ex)).length,
  };
}

/** 오늘 끝낸 단계 수 */
export function doneToday(tasks: Task[], today: string = kstToday()): number {
  let n = 0;
  tasks.forEach(t => (t.steps || []).forEach(s => { if (s.status === '완료' && stepEx(s).done_at === today) n += 1; }));
  return n;
}

/** 줄 맨 앞 배지 글자 */
export function dayLabel(d: number | null): string {
  if (d === null) return '—';
  if (d < 0) return `${-d}일 지남`;
  if (d === 0) return '오늘';
  return `${d}일 남음`;   // 「D-2」 대신 — 고객 · FP 화면과 같은 말(2026-10-12 검수)
}

export function waitWho(ex: FlowExtras, task: Task): string {
  if (task.status === 'FP요청대기') return 'FP 요청 대기';
  const w = ex.wait_for && ex.wait_for.length ? ex.wait_for : ['업체', 'FP'];
  return `${w.join('·')} 회신 대기`;
}

/** 담당 FP 없는 업체 · WM 없는 FP (진행 중 업체만 셈) */
export function missingLinks(companies: Company[], tasks: Task[], fps: { id: string; wm_id: string | null }[]): { noFp: number; noWm: number } {
  const active = new Set(tasks.filter(t => !isOff(t)).map(t => t.company_id));
  const noFp = companies.filter(c => active.has(c.id) && (!c.fps || c.fps.length === 0) && !c.referral_type).length;
  const usedFp = new Set(companies.filter(c => active.has(c.id)).flatMap(c => (c.fps || []).map(f => f.fp_id)));
  const noWm = fps.filter(f => usedFp.has(f.id) && !f.wm_id).length;
  return { noFp, noWm };
}

/** 업체 상태(목록 · 필터) — 진행 중 업무가 있으면 진행중 · 모두 완료면 완료 · 완료/중단뿐이고 중단이 있으면 중단(업체 포기) · 보류만 남으면 보류 */
export function companyStatus(c: Company, tasks: Task[]): string {
  const my = tasks.filter(t => t.company_id === c.id);
  if (my.some(t => t.status === '진행' || t.status === 'FP요청대기')) return '진행중';
  if (my.length > 0 && my.every(t => t.status === '완료')) return '완료';
  // 이번 회차 마감(미선정)만 있으면 업체 포기가 아니라 다음 회차를 기다림 → 보류
  if (my.length > 0 && my.every(t => t.status === '완료' || t.status === '중단')) return my.some(t => t.status === '중단' && !isClosed(t)) ? '중단' : my.some(isClosed) ? '보류' : '완료';
  if (my.some(t => t.status === '보류')) return '보류';
  return c.tm_status || '대기';
}

/** 업체 카드 한 줄 — 가장 급한 업무의 지금 단계: 「사업계획서 · 3일 지남」 · 「재무제표 · 회신 16일째」 · 「중단 · 비용 부담」 */
export function companyLine(c: Company, tasks: Task[], today: string = kstToday()): { text: string; color: string; kind: 'over' | 'today' | 'wait' | 'mine' | 'stop' | 'none' } | null {
  const my = tasks.filter(t => t.company_id === c.id);
  const rows = my.map(t => rowOf(t, c, today)).filter(r => r.kind !== 'off');
  const rank = (r: FlowRow) => (r.kind === 'mine' && r.d !== null && r.d < 0 ? [0, r.d] : r.kind === 'mine' && r.d === 0 ? [1, 0] : r.kind === 'wait' ? [2, -r.waitDays] : r.kind === 'mine' && r.d !== null ? [3, r.d] : [4, 0]);
  const best = rows.sort((a, b) => { const x = rank(a); const y = rank(b); return x[0] - y[0] || x[1] - y[1]; })[0];
  if (best) {
    const nm = best.step ? best.step.name : best.task.type;
    if (best.kind === 'wait') return { text: claimOf(best.ex) ? `${nm} · 답 왔대요 · 확인하세요` : `${nm} · 회신 ${best.waitDays}일째`, color: claimOf(best.ex) ? '#92400E' : '#0A7D74', kind: 'wait' };
    if (best.d === null) return { text: `${nm} · 진행 중`, color: '#2563EB', kind: 'mine' };
    if (best.d < 0) return { text: `${nm} · ${-best.d}일 지남`, color: '#DC2626', kind: 'over' };
    if (best.d === 0) return { text: `${nm} · 오늘 마감`, color: '#C2410C', kind: 'today' };
    return { text: `${nm} · ${best.d}일 남음`, color: '#2563EB', kind: 'mine' };
  }
  const closed = my.find(isClosed);
  if (closed && my.every(t => t.status === '완료' || t.status === '중단')) return { text: String(closed.stop_reason || CLOSE_PREFIX).split(' · ').slice(0, 2).join(' · '), color: '#475569', kind: 'stop' };
  const stopped = my.find(t => t.status === '중단');
  if (stopped && my.every(t => t.status === '완료' || t.status === '중단')) return { text: `중단 · ${stopped.stop_reason || ''}`.replace(/ · $/, ''), color: '#6B7280', kind: 'stop' };
  return null;
}

